import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { usePanel } from 'contexts/panelContext';
import { Todo } from 'types/Todo';
import { dateFormat } from 'utils/dateFormat';
import { KnowledgeBaseArticleLink, KnowledgeBaseDirectoryLink } from 'components/knowledgeBase/KnowledgeBaseLink';
import { IconColor } from 'components/_new/Icon';

interface TodoItemProps extends Todo {
	isClickableWrapper?: boolean;
}

const TodoItem = ({ type, variables, isClickableWrapper = false, ...props }: TodoItemProps) => {
	const { t } = useTranslation();
	const { organizationName, programName, type: panelType } = useParams();

	const { currentApplicationData, panelLinkPrefix } = usePanel();

	const icon = useMemo(() => {
		switch (type) {
			case 'application_during_deletion':
				return <IconColor name="user-times" />;
			case 'pair_during_deletion':
				return <IconColor name="users-times" />;
			case 'read_guides':
				return <IconColor name="calendar-check" />;
			case 'no_accepted_pair':
				return <IconColor name="user-plus" />;
			case 'wait_for_mentee':
			case 'confirm_entry_meeting':
				return <IconColor name="check-write" />;
			case 'arrange_first_session_date':
				return <IconColor name="calendar-plus" />;
			case 'contract_initial_sign_required':
				return <IconColor name="text-sign" />;
			case 'contract_goals_sign_required':
				return <IconColor name="checklist-write" />;
			case 'session_reminder':
				return <IconColor name="calendar-check" />;
			case 'add_session_date':
				return <IconColor name="calendar-plus" />;
			case 'final_report_mentee':
				return <IconColor name="download" />;
			case 'final_report_mentor':
				return <IconColor name="text-write" />;
			case 'evaluation_survey_required':
				return <IconColor name="checklist-write-blue" />;
			case 'li_label_info':
				return <IconColor name="linkedin" />;
			default:
				return <i />;
		}
	}, [type]);

	const getLinkProps = (): { href: string } | null => {
		switch (type) {
			case 'read_guides':
			case 'no_accepted_pair':
			case 'wait_for_mentee':
			case 'confirm_entry_meeting':
				return null;
			case 'arrange_first_session_date':
				return {
					href: `/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/session/${variables.sessionId}`,
				};
			case 'contract_initial_sign_required':
				return {
					href: `/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/contract`,
				};
			case 'contract_goals_sign_required':
				return {
					href: `/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/mentee-plan`,
				};
			case 'session_reminder':
				return {
					href: `/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/session/${variables.sessionId}`,
				};
			case 'final_report_mentee':
				return {
					href: `/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/process-summary`,
				};
			case 'final_report_mentor':
				return {
					href: `/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/final-report`,
				};
			case 'evaluation_survey_required':
				return {
					href: `/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/session/${variables.sessionId}/evaluation-mentee#content`,
				};
			case 'li_label_info':
				return {
					href: `/panel/${organizationName}/${programName}/${panelType}/linkedin-badge`,
				};
			case 'add_session_date':
				return {
					href: `/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/session/${variables.sessionId}`,
				};
			default:
				return null;
		}
	};

	const content = useMemo(() => {
		switch (type) {
			case 'application_during_deletion':
				return (
					<span className="text-error">
						<Trans t={t} i18nKey="userPanel.todo.appliationDuringDeletion" />
					</span>
				);
			case 'pair_during_deletion':
				return (
					<span className="text-error">
						<Trans t={t} i18nKey="userPanel.todo.pairDuringDeletion" />
					</span>
				);
			case 'read_guides':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.readGuides"
						components={[
							<KnowledgeBaseDirectoryLink
								label="mentiway-guides"
								render={({ to: { pathname }, ...props }) => (
									<Link
										to={{ pathname: `/${panelLinkPrefix}/${pathname}` }}
										className="text-underline font-semibold text-navy-blue"
										{...props}
									/>
								)}
							/>,
							<KnowledgeBaseArticleLink
								label="mentees-role"
								render={({ to: { pathname }, ...props }) => (
									<Link
										to={{ pathname: `/${panelLinkPrefix}/${pathname}` }}
										className="text-underline font-semibold text-navy-blue"
										{...props}
									/>
								)}
							/>,
						]}
					/>
				);
			case 'no_accepted_pair':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.noAcceptedPair"
						components={[
							<Link
								to={`/panel/${organizationName}/${programName}/${panelType}/pairing`}
								className="text-underline font-semibold text-navy-blue"
							/>,
						]}
					/>
				);
			case 'wait_for_mentee':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.waitForMentee"
						components={[
							<KnowledgeBaseDirectoryLink
								label="mentiway-guides"
								render={({ to: { pathname }, ...props }) => (
									<Link
										to={{ pathname: `/${panelLinkPrefix}/${pathname}` }}
										className="text-underline font-semibold text-navy-blue"
										{...props}
									/>
								)}
							/>,
							<KnowledgeBaseArticleLink
								label="mentors-role"
								render={({ to: { pathname }, ...props }) => (
									<Link
										to={{ pathname: `/${panelLinkPrefix}/${pathname}` }}
										className="text-underline font-semibold text-navy-blue"
										{...props}
									/>
								)}
							/>,
						]}
					/>
				);
			case 'confirm_entry_meeting':
				return (
					<Trans
						t={t}
						i18nKey={
							currentApplicationData?.applicationRole === 'mentor'
								? 'userPanel.todo.confirmEntryMeetingWithMentee'
								: 'userPanel.todo.confirmEntryMeetingWithMentor'
						}
						values={variables}
						components={[
							<Link
								to={`/panel/${organizationName}/${programName}/${panelType}/pairing`}
								className="text-underline font-semibold text-navy-blue"
							/>,
						]}
					/>
				);
			case 'arrange_first_session_date':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.arrangeFirstSessionDate"
						values={{
							...variables,
						}}
						components={[
							isClickableWrapper ? (
								<span />
							) : (
								<Link
									to={`/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/session/${variables.sessionId}`}
									className="text-underline font-semibold text-navy-blue"
								/>
							),
						]}
					/>
				);
			case 'contract_initial_sign_required':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.contractInitialSignRequired"
						values={variables}
						components={[
							isClickableWrapper ? (
								<span />
							) : (
								<Link
									to={`/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/contract`}
									className="text-underline font-semibold text-navy-blue"
								/>
							),
						]}
					/>
				);
			case 'contract_goals_sign_required':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.contractGoalsSignRequired"
						values={variables}
						components={[
							isClickableWrapper ? (
								<span />
							) : (
								<Link
									to={`/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/mentee-plan`}
									className="text-underline font-semibold text-navy-blue"
								/>
							),
						]}
					/>
				);
			case 'session_reminder':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.sessionReminder"
						values={{
							...variables,
							sessionDate: dateFormat(String(variables.sessionDate), 'full'),
						}}
						components={[
							isClickableWrapper ? (
								<span />
							) : (
								<Link
									to={`/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/session/${variables.sessionId}`}
									className="text-underline font-semibold text-navy-blue"
								/>
							),
						]}
					/>
				);
			case 'final_report_mentee':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.finalReportMentee"
						values={variables}
						components={[
							isClickableWrapper ? (
								<span />
							) : (
								<Link
									to={`/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/process-summary`}
									className="text-underline font-semibold text-navy-blue"
								/>
							),
						]}
					/>
				);
			case 'final_report_mentor':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.finalReportMentor"
						values={variables}
						components={[
							isClickableWrapper ? (
								<span />
							) : (
								<Link
									to={`/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/final-report`}
									className="text-underline font-semibold text-navy-blue"
								/>
							),
						]}
					/>
				);
			case 'evaluation_survey_required':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.evaluationSurvey"
						values={variables}
						components={[
							isClickableWrapper ? (
								<span />
							) : (
								<Link
									to={`/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/session/${variables.sessionId}/evaluation-mentee#content`}
									className="text-underline font-semibold text-navy-blue"
								/>
							),
						]}
					/>
				);
			case 'li_label_info':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.liLabelInfo"
						values={variables}
						components={[
							isClickableWrapper ? (
								<span />
							) : (
								<Link
									to={`/panel/${organizationName}/${programName}/${panelType}/linkedin-badge`}
									className="text-underline font-semibold text-navy-blue"
								/>
							),
						]}
					/>
				);
			case 'add_session_date':
				return (
					<Trans
						t={t}
						i18nKey="userPanel.todo.addSessionDate"
						values={{
							...variables,
						}}
						components={[
							isClickableWrapper ? (
								<span />
							) : (
								<Link
									to={`/panel/${organizationName}/${programName}/${panelType}/pair/${variables.pairId}/session/${variables.sessionId}`}
									className="text-underline font-semibold text-navy-blue"
								/>
							),
						]}
					/>
				);
			default:
				return '[not formated todo]';
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [type, variables, t, isClickableWrapper]);

	const linkProps = getLinkProps();

	if (isClickableWrapper) {
		const innerContent = (
			<div className="flex flex-row gap-2">
				<div
					className=" flex-0 flex flex-row justify-content-center align-items-center py-1"
					style={{ minWidth: '20px', width: '20px' }}
				>
					{icon}
				</div>
				<div className="text-left flex-1 ml-1">{content}</div>
			</div>
		);

		return (
			<li className="p-0" {...props}>
				<Link
					to={linkProps?.href || '#'}
					style={{ textDecoration: 'none', color: 'inherit', display: 'block', padding: '0.2rem 0' }}
				>
					<div className="mobile-bottom-nav-todo-wrapper">{innerContent}</div>
				</Link>
			</li>
		);
	}

	return (
		<li className="p-0 mb-3" {...props}>
			<div className="flex flex-row gap-3">
				<div className="icon-container icon-container-24 text-rose my-1">{icon}</div>
				<div className="text-left flex-1">{content}</div>
			</div>
		</li>
	);
};

export default TodoItem;
